import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { VbzInputText } from "@smiles/voebiz-ui-kit";

import { useUser } from "../../../../context/UserContext";

import { RegisterContent } from "../../../../components";

import { checkZipCode } from "../../../../services/checkZipCode";

import { AddressForm, handleFormChangeF } from "../type";

interface CompanyAddressProps {
  data: AddressForm;
  button: () => JSX.Element;
  onChange: handleFormChangeF;
}

const CompanyAddress: FC<CompanyAddressProps> = ({ data, button, onChange }) => {
  const { t } = useTranslation("register_company");
  const {
    data: { apiDomain },
  } = useUser();
  const [formData, setFormData] = useState<AddressForm>({ ...data });
  const [validatedCep, setValidatedCep] = useState<boolean>(false);

  async function getZipCode(getValueZipCode: string) {
    setValidatedCep(true);
    const response = await checkZipCode(getValueZipCode, apiDomain);
    if (response.message) {
      setValidatedCep(false);
      return;
    }
    const { State, City, StreetName, Country } = response;
    setFormData((prevState) => ({
      ...prevState,
      state: State,
      city: City,
      streetAddress: StreetName,
      country: Country,
    }));
  }

  const handleKeyPressNumber = (e: React.KeyboardEvent) => {
    if (!(e.key >= "0" && e.key <= "9") && !(e.key === "-" || e.key === " ")) {
      e.preventDefault();
    }
  };

  const formatCep = (cep: string) => {
    let doc = cep;
    doc = doc.replace(/\D/g, "");
    doc = doc.replace(/(\d{2})(\d)/, "$1.$2");
    doc = doc.replace(/(\d{3})(\d{1,3})$/, "$1-$2");
    return doc;
  };

  const handleChangeZipCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidatedCep(false);
    let doc = e.target.value;
    doc = doc.replace(/\D/g, "");
    doc = doc.replace(/(\d{2})(\d)/, "$1.$2");
    doc = doc.replace(/(\d{3})(\d{1,3})$/, "$1-$2");
    setFormData((prevState) => ({ ...prevState, zipCode: doc, state: "", city: "", streetAddress: "", country: "" }));
    if (doc?.length === 10) {
      getZipCode(doc.replace(/[^0-9]/g, ""));
    }
  };

  const handleChangeAddressNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prevState) => ({ ...prevState, streetAddressNumber: value }));
  };

  const handleChangeComplement = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prevState) => ({ ...prevState, complement: value }));
  };

  useEffect(() => {
    onChange("address", formData);
  }, [formData]);

  useEffect(() => {
    if (formData.zipCode.length < 3) {
      setValidatedCep(true);
    }
  }, [formData.zipCode]);
  useEffect(() => {
    if (formData.zipCode.length === 8) {
      setValidatedCep(true);
    }
  }, []);

  return (
    <div className="voebiz-register-cnpj voebiz-register-cnpj-section-2">
      <div className="voebiz-title-wrapper-desktop">
        <h3 className="title-wrapper" id="lbl_titleCompany">
          {t("data_titleSection2")}
        </h3>
      </div>
      <div className="voebiz-title-wrapper-mobile">
        <h3 className="title-wrapper" id="lbl_titleCompany">
          {t("data_titleSection2Mobile")}
        </h3>
      </div>
      <div className="voebiz-content-wrapper">
        <RegisterContent
          idMessage="lbl_msgInformativeCompanyAddress"
          text={t("address_description")}
          label={t("address_label")}
        >
          <div className="voebiz-company-container-address-fields">
            <div className="voebiz-company-container-address-fields-right">
              <div className="inputTextContainer">
                <VbzInputText
                  id="inp_zipCode"
                  placeholder={t("input.text.zipCode.placeholder")}
                  maxLength={10}
                  inputMode="numeric"
                  textLabel={t("input.text.zipCode.label")}
                  errorMessage={t("input.text.zipCode.error")}
                  onChange={handleChangeZipCode}
                  onFocus={handleChangeZipCode}
                  onBlur={handleChangeZipCode}
                  value={formatCep(formData.zipCode)}
                  forceError={!validatedCep}
                  onKeyPress={handleKeyPressNumber}
                  regex={/[^0-9A-Za-záàâãéèêíïóôõöúüçñÁÀÂÃÉÈÍÏÓÔÕÖÚÜÇÑ .-]+/g}
                />
              </div>
              <div className="voebiz-company-container-address-fields columns">
                <div className="inputTextContainer">
                  <VbzInputText
                    id="inp_state_fu"
                    placeholder={t("input.text.state_fu.placeholder")}
                    value={formData.state}
                    disabled
                    textLabel={t("input.text.state_fu.label")}
                  />
                </div>

                <div className="inputTextContainer">
                  <VbzInputText
                    id="inp_city"
                    placeholder={t("input.text.city.placeholder")}
                    value={formData.city}
                    disabled
                    textLabel={t("input.text.city.label")}
                  />
                </div>
              </div>
              <div className="inputTextContainer">
                <VbzInputText
                  id="inp_address"
                  placeholder={t("input.text.address.placeholder")}
                  value={formData.streetAddress}
                  disabled
                  textLabel={t("input.text.address.label")}
                />
              </div>

              <div className="voebiz-company-container-address-fields columns">
                <div className="inputTextContainer">
                  <VbzInputText
                    id="inp_number"
                    placeholder={t("input.text.number.placeholder")}
                    onChange={handleChangeAddressNumber}
                    value={formData.streetAddressNumber}
                    textLabel={t("input.text.number.label")}
                  />
                </div>

                <div className="inputTextContainer">
                  <VbzInputText
                    id="inp_extra_info"
                    placeholder={t("input.text.extra_info.placeholder")}
                    onChange={handleChangeComplement}
                    value={formData.complement}
                    textLabel={t("input.text.extra_info.label")}
                  />
                </div>
              </div>
              {button()}
            </div>
          </div>
        </RegisterContent>
      </div>
    </div>
  );
};

export default CompanyAddress;
